import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react'
import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/cn'

/** The red asterisk of a compulsory field, for headings that are not a Field label. */
export function RequiredMark() {
  return (
    <span className="ml-1 text-danger" aria-hidden="true">
      *
    </span>
  )
}

export function Field({
  label,
  error,
  htmlFor,
  required,
  children,
  className,
}: {
  label?: string
  error?: string
  htmlFor?: string
  /** Marks the field compulsory with the same red asterisk as "Size quantities". */
  required?: boolean
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <label htmlFor={htmlFor} className="block text-sm text-foreground">
          {label}
          {required && <RequiredMark />}
        </label>
      )}
      {children}
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  )
}

/* Icewall input: h-10, rounded-md, bg slate-50, focus ring foreground/5 offset 2.
   16px text below sm - anything smaller makes iOS Safari zoom in on focus. */
export const inputClass =
  'h-10 w-full rounded-md border border-foreground/20 bg-background px-3 text-base sm:text-sm text-foreground placeholder:text-foreground/70 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/5 focus-visible:ring-offset-2 disabled:opacity-60'

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(inputClass, className)} {...props} />
}

/* Same recipe without the fixed h-10: cn() cannot override a height that is
   already in inputClass, so the textarea carries its own string. */
export const textareaClass =
  'min-h-20 w-full rounded-md border border-foreground/20 bg-background px-3 py-2 text-base sm:text-sm text-foreground placeholder:text-foreground/70 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/5 focus-visible:ring-offset-2 disabled:opacity-60'

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(textareaClass, className)} {...props} />
}

export function PasswordInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  const [show, setShow] = useState(false)
  return (
    <div className="relative">
      <input type={show ? 'text' : 'password'} className={cn(inputClass, 'pr-10', className)} {...props} />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute inset-y-0 right-0 flex items-center pr-3 text-foreground/50 hover:text-foreground"
        tabIndex={-1}
      >
        {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  )
}
